import React, { useEffect, useState } from 'react';
import { Loader2, Trash2, RotateCcw, Flag, EyeOff, Images, Star, MessageCircle } from 'lucide-react';
import api, { mediaUrl } from '../../lib/api';
import { useToast } from '../../hooks/use-toast';

const AdminEventPhotos = () => {
  const { toast } = useToast();
  const [scope, setScope] = useState('reported');
  const [photos, setPhotos] = useState([]);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pastList, setPastList] = useState([]);
  const [recapSlug, setRecapSlug] = useState('');
  const [savingRecap, setSavingRecap] = useState(false);

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get('/admin/event-photos', { params: { scope } }).then((r) => r.data?.photos || []).catch(() => []),
      api.get('/admin/event-photos/comments', { params: { scope } }).then((r) => r.data?.comments || []).catch(() => []),
    ]).then(([ph, cm]) => { setPhotos(ph); setComments(cm); }).finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [scope]);
  useEffect(() => {
    api.get('/programs', { params: { past: true, limit: 200 } }).then((r) => setPastList(r.data || [])).catch(() => {});
    api.get('/admin/event-photos/recap').then((r) => setRecapSlug(r.data?.slug || '')).catch(() => {});
  }, []);

  const saveRecap = async (slug) => {
    setSavingRecap(true);
    try { await api.put('/admin/event-photos/recap', { slug: slug || null }); setRecapSlug(slug); toast({ title: slug ? 'Homepage recap set' : 'Recap cleared' }); }
    catch (e) { toast({ title: 'Failed', description: e?.response?.data?.detail, variant: 'destructive' }); }
    finally { setSavingRecap(false); }
  };

  const restore = async (id) => {
    try { await api.post(`/admin/event-photos/${id}/restore`); toast({ title: 'Photo restored' }); load(); }
    catch (e) { toast({ title: 'Failed', description: e?.response?.data?.detail, variant: 'destructive' }); }
  };
  const del = async (id) => {
    if (!window.confirm('Permanently delete this photo?')) return;
    try { await api.delete(`/admin/event-photos/${id}`); toast({ title: 'Photo deleted' }); setPhotos((p) => p.filter((x) => x.id !== id)); }
    catch (e) { toast({ title: 'Failed', description: e?.response?.data?.detail, variant: 'destructive' }); }
  };
  const restoreComment = async (id) => {
    try { await api.post(`/admin/event-photos/comments/${id}/restore`); toast({ title: 'Comment restored' }); load(); }
    catch (e) { toast({ title: 'Failed', description: e?.response?.data?.detail, variant: 'destructive' }); }
  };
  const delComment = async (id) => {
    if (!window.confirm('Delete this comment?')) return;
    try { await api.delete(`/admin/event-photos/comments/${id}`); toast({ title: 'Comment deleted' }); setComments((c) => c.filter((x) => x.id !== id)); }
    catch (e) { toast({ title: 'Failed', description: e?.response?.data?.detail, variant: 'destructive' }); }
  };

  return (
    <div className="space-y-6" data-testid="admin-event-photos">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-serif-display text-3xl text-burgundy flex items-center gap-2"><Images className="w-6 h-6" /> Event Photos</h1>
          <p className="text-mahogany/70 text-sm mt-1">Attendee-shared photos &amp; comments. Reported items appear here — restore the good ones, delete anything offensive.</p>
        </div>
        <div className="inline-flex rounded-full bg-white/70 border border-rose/50 p-1">
          <button onClick={() => setScope('reported')} data-testid="scope-reported"
            className={`px-4 py-1.5 rounded-full text-sm transition-colors ${scope === 'reported' ? 'bg-mahogany text-cream' : 'text-mahogany/70'}`}>Reported</button>
          <button onClick={() => setScope('all')} data-testid="scope-all"
            className={`px-4 py-1.5 rounded-full text-sm transition-colors ${scope === 'all' ? 'bg-mahogany text-cream' : 'text-mahogany/70'}`}>All shared</button>
        </div>
      </div>

      {/* Homepage recap selector */}
      <div className="p-4 rounded-2xl bg-white/70 border border-rose/50 flex flex-wrap items-center gap-3" data-testid="recap-selector">
        <div className="flex items-center gap-2 text-burgundy"><Star className="w-4 h-4 text-rose-italic" /><span className="text-sm font-medium">Homepage recap</span></div>
        <select value={recapSlug} onChange={(e) => saveRecap(e.target.value)} disabled={savingRecap} data-testid="recap-select"
          className="bg-cream rounded-xl border border-rose/50 px-3 py-2 text-sm text-burgundy focus:outline-none focus:border-mahogany min-w-[240px]">
          <option value="">— None (hide recap section) —</option>
          {pastList.map((p) => <option key={p.slug} value={p.slug}>{p.title}</option>)}
        </select>
        {savingRecap && <Loader2 className="w-4 h-4 animate-spin text-mahogany" />}
        <span className="text-xs text-mahogany/60">Showcases this past event &amp; its top community photos on the homepage.</span>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-mahogany" /></div>
      ) : (
        <>
          <h2 className="font-serif-display text-xl text-burgundy">Photos</h2>
          {photos.length === 0 ? (
            <div className="text-center text-mahogany/60 py-8">{scope === 'reported' ? 'No reported photos. 🌸' : 'No attendee photos shared yet.'}</div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {photos.map((ph) => (
                <div key={ph.id} className="rounded-2xl overflow-hidden bg-white/70 border border-rose/40" data-testid={`mod-photo-${ph.id}`}>
                  <img src={mediaUrl(ph.image_url)} alt="" className="w-full aspect-video object-cover" />
                  <div className="p-3 space-y-2">
                    <div className="flex items-center gap-2 flex-wrap">
                      {ph.status === 'hidden' && <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 inline-flex items-center gap-1"><EyeOff className="w-3 h-3" /> Auto-hidden</span>}
                      {ph.report_count > 0 && <span className="text-[10px] px-2 py-0.5 rounded-full bg-red-100 text-red-800 inline-flex items-center gap-1"><Flag className="w-3 h-3" /> {ph.report_count} report{ph.report_count > 1 ? 's' : ''}</span>}
                      {ph.status === 'visible' && ph.report_count === 0 && <span className="text-[10px] px-2 py-0.5 rounded-full bg-green-100 text-green-800">Live</span>}
                    </div>
                    {ph.caption && <p className="text-sm text-burgundy/90">{ph.caption}</p>}
                    <p className="text-xs text-mahogany/60">By {ph.user_name} · {ph.program_title || ph.program_slug}</p>
                    <div className="flex gap-2 pt-1">
                      {(ph.status === 'hidden' || ph.report_count > 0) && (
                        <button onClick={() => restore(ph.id)} data-testid={`mod-restore-${ph.id}`}
                          className="text-xs px-3 py-1.5 rounded-full bg-green-100 text-green-800 hover:bg-green-200 transition-colors inline-flex items-center gap-1.5"><RotateCcw className="w-3.5 h-3.5" /> Keep &amp; clear</button>
                      )}
                      <button onClick={() => del(ph.id)} data-testid={`mod-delete-${ph.id}`}
                        className="text-xs px-3 py-1.5 rounded-full bg-red-100 text-red-800 hover:bg-red-200 transition-colors inline-flex items-center gap-1.5"><Trash2 className="w-3.5 h-3.5" /> Delete</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          <h2 className="font-serif-display text-xl text-burgundy flex items-center gap-2 pt-2"><MessageCircle className="w-5 h-5" /> Comments</h2>
          {comments.length === 0 ? (
            <div className="text-center text-mahogany/60 py-6">{scope === 'reported' ? 'No reported comments.' : 'No comments yet.'}</div>
          ) : (
            <div className="space-y-2">
              {comments.map((c) => (
                <div key={c.id} className="p-3 rounded-2xl bg-white/70 border border-rose/40 flex items-start justify-between gap-3" data-testid={`mod-comment-${c.id}`}>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-0.5">
                      {c.status === 'hidden' && <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">Auto-hidden</span>}
                      {c.report_count > 0 && <span className="text-[10px] px-2 py-0.5 rounded-full bg-red-100 text-red-800">{c.report_count} report{c.report_count > 1 ? 's' : ''}</span>}
                    </div>
                    <p className="text-sm text-burgundy/90">{c.text}</p>
                    <p className="text-xs text-mahogany/60">By {c.user_name}</p>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    {(c.status === 'hidden' || c.report_count > 0) && (
                      <button onClick={() => restoreComment(c.id)} data-testid={`mod-comment-restore-${c.id}`}
                        className="text-xs px-3 py-1.5 rounded-full bg-green-100 text-green-800 hover:bg-green-200 transition-colors">Keep</button>
                    )}
                    <button onClick={() => delComment(c.id)} data-testid={`mod-comment-delete-${c.id}`}
                      className="text-xs px-3 py-1.5 rounded-full bg-red-100 text-red-800 hover:bg-red-200 transition-colors">Delete</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AdminEventPhotos;
